export interface CareerPath {
  id: string;
  title: string;
  category: string;
  keywords: string[];
  salary: {
    entry_min: number;
    entry_max: number;
  };
  description: string;
  dailyActivities: string[];
  requiredSkills: string[];
  niceToHave: string[];
  commonTransitionsFrom: string[];
}

// Import unified career database (650+ careers from O*NET + curated)
import { ALL_CAREER_PATHS } from './data/allCareers';

// Import diagnostic questions for scoring
import { DIAGNOSTIC_QUESTIONS } from './diagnosticQuestions';

// Export the unified careers as the main career paths
// This combines 637 O*NET careers with 50+ manually curated careers
export const CAREER_PATHS: CareerPath[] = ALL_CAREER_PATHS;

// Keep original 3 careers for reference/backward compatibility
export const ORIGINAL_CAREER_PATHS: CareerPath[] = [
  {
    id: 'customer_success',
    title: 'Customer Success Specialist',
    category: 'Customer Service',
    keywords: ['customer_service', 'communication', 'problem_solving', 'tech', 'people', 'support'],
    salary: {
      entry_min: 40000,
      entry_max: 55000
    },
    description: 'Help customers succeed with products/services through proactive outreach, troubleshooting, and relationship building.',
    dailyActivities: [
      'Respond to customer emails, chats, and calls',
      'Troubleshoot product issues',
      'Track customer satisfaction metrics',
      'Conduct onboarding calls for new customers'
    ],
    requiredSkills: [
      'customer_service',
      'communication',
      'problem_solving',
      'basic tech literacy'
    ],
    niceToHave: [
      'CRM experience',
      'Salesforce',
      'data entry'
    ],
    commonTransitionsFrom: [
      'retail',
      'restaurant',
      'call center',
      'hospitality'
    ]
  },
  {
    id: 'logistics_coordinator',
    title: 'Logistics Coordinator',
    category: 'Operations',
    keywords: ['operations', 'logistics', 'organized', 'warehouse', 'shipping', 'inventory'],
    salary: {
      entry_min: 38000,
      entry_max: 52000
    },
    description: 'Coordinate the movement of goods, manage inventory, and ensure timely delivery of products.',
    dailyActivities: [
      'Track shipments and inventory levels',
      'Coordinate with warehouses and vendors',
      'Resolve shipping delays and issues',
      'Maintain accurate records in tracking systems'
    ],
    requiredSkills: [
      'organization',
      'attention to detail',
      'time management',
      'basic computer skills'
    ],
    niceToHave: [
      'forklift certification',
      'supply chain knowledge',
      'Excel proficiency'
    ],
    commonTransitionsFrom: [
      'warehouse',
      'delivery driver',
      'retail',
      'manufacturing'
    ]
  },
  {
    id: 'healthcare_support',
    title: 'Healthcare Support Specialist',
    category: 'Healthcare',
    keywords: ['care', 'healthcare', 'people', 'support', 'medical', 'patient'],
    salary: {
      entry_min: 35000,
      entry_max: 48000
    },
    description: 'Support healthcare facilities with patient coordination, administrative tasks, and ensuring smooth operations.',
    dailyActivities: [
      'Schedule patient appointments',
      'Maintain patient records',
      'Coordinate between patients and medical staff',
      'Handle insurance verification'
    ],
    requiredSkills: [
      'communication',
      'empathy',
      'organization',
      'confidentiality'
    ],
    niceToHave: [
      'medical terminology',
      'HIPAA knowledge',
      'EMR systems'
    ],
    commonTransitionsFrom: [
      'caregiver',
      'nanny',
      'customer service',
      'admin assistant'
    ]
  }
];

export interface ScoringResult {
  pathId: string;
  score: number;
  matchedKeywords: string[];
}

export function scoreUserForPaths(answers: Record<string, unknown>): ScoringResult[] {
  const userKeywords: string[] = [];

  // Extract keywords from Q1 (core capability)
  if (typeof answers.q1_core_capability === 'string') {
    const q1Option = DIAGNOSTIC_QUESTIONS[0].options?.find(o => o.id === answers.q1_core_capability);
    if (q1Option) {
      userKeywords.push(...q1Option.keywords);
    }
  }

  // Extract keywords from Q3 (work style)
  if (typeof answers.q3_work_style === 'string') {
    const q3Option = DIAGNOSTIC_QUESTIONS[2].options?.find(o => o.id === answers.q3_work_style);
    if (q3Option) {
      userKeywords.push(...q3Option.keywords);
    }
  }

  // Extract keywords from Q4 (natural strengths) - multiple choice
  if (Array.isArray(answers.q4_natural_strengths)) {
    const q4Options = DIAGNOSTIC_QUESTIONS[3].options || [];
    answers.q4_natural_strengths.forEach((strengthId: unknown) => {
      if (typeof strengthId !== 'string') {
        return;
      }
      const option = q4Options.find(o => o.id === strengthId);
      if (option) {
        userKeywords.push(...option.keywords);
      }
    });
  }

  // Score each career path
  const results: ScoringResult[] = CAREER_PATHS.map(path => {
    const matchedKeywords = userKeywords.filter(uk => 
      path.keywords.some(pk => pk.toLowerCase() === uk.toLowerCase())
    );

    const score = Math.round((matchedKeywords.length / path.keywords.length) * 100);

    return {
      pathId: path.id,
      score,
      matchedKeywords
    };
  });

  // Sort by score descending
  return results.sort((a, b) => b.score - a.score);
}

export function getPathById(id: string): CareerPath | undefined {
  return CAREER_PATHS.find(p => p.id === id);
}

// Simple scoring function for API usage
export function scoreCareerMatch(answers: Record<string, unknown>, career: CareerPath): number {
  const userKeywords: string[] = [];

  // Extract keywords from Q1 (core capability)
  if (typeof answers.q1_core_capability === 'string') {
    const q1Option = DIAGNOSTIC_QUESTIONS[0].options?.find(o => o.id === answers.q1_core_capability);
    if (q1Option) {
      userKeywords.push(...q1Option.keywords);
    }
  }

  // Extract keywords from Q3 (work style)
  if (typeof answers.q3_work_style === 'string') {
    const q3Option = DIAGNOSTIC_QUESTIONS[2].options?.find(o => o.id === answers.q3_work_style);
    if (q3Option) {
      userKeywords.push(...q3Option.keywords);
    }
  }

  // Extract keywords from Q4 (natural strengths)
  if (Array.isArray(answers.q4_natural_strengths)) {
    const q4Options = DIAGNOSTIC_QUESTIONS[3].options || [];
    answers.q4_natural_strengths.forEach((strengthId: unknown) => {
      if (typeof strengthId !== 'string') {
        return;
      }
      const option = q4Options.find(o => o.id === strengthId);
      if (option) {
        userKeywords.push(...option.keywords);
      }
    });
  }

  const matchedKeywords = userKeywords.filter(uk => 
    career.keywords.some(pk => pk.toLowerCase() === uk.toLowerCase())
  );

  return Math.round((matchedKeywords.length / career.keywords.length) * 100);
}

